import React, { useRef, useEffect, useState, memo } from 'react';
import { loadImage, getCachedImage } from './imageCache';
import { getRefImage, getCachedThumbnail, setCachedThumbnail } from './thumbnailCache';
import renderLookComposite from './renderLookComposite';

// Font loading: load fonts from S3 via @font-face (shared with LookCanvas)
const loadedFonts = new Set();
const loadFont = async (fontFilename, fontS3Path) => {
  if (!fontFilename || loadedFonts.has(fontFilename)) return;
  loadedFonts.add(fontFilename);
  const familyName = fontFilename.replace(/\.[^.]+$/, '');
  try {
    const s3Key = (fontS3Path || 's3://shoobphoto/fonts/') +
      ((fontS3Path || '').endsWith('/') ? '' : '/') + fontFilename;
    const { fetchPresignedUrl } = await import('./api');
    const url = await fetchPresignedUrl(s3Key.replace(/^s3:\/\/[^/]+\//, ''));
    const face = new FontFace(familyName, `url(${url})`);
    const loaded = await face.load();
    document.fonts.add(loaded);
  } catch (err) {
    loadedFonts.delete(fontFilename);
  }
};

const LookPreviewCanvas = memo(({ look, width, height, transparent, allLooks, showGuides }) => {
  const canvasRef = useRef(null);
  const [refImg, setRefImg] = useState(null);
  const [canvasBgRefImg, setCanvasBgRefImg] = useState(null);
  const [overlayImg, setOverlayImg] = useState(null);
  const [canvasBgImg, setCanvasBgImg] = useState(null);
  const [collagePhotoImgs, setCollagePhotoImgs] = useState({});
  const [fontLoadTick, setFontLoadTick] = useState(0);

  const lookW = look.canvas_width || 2400;
  const lookH = look.canvas_height || 3000;
  const lookAspect = lookW / lookH;
  const containerAspect = width / height;

  let cw, ch;
  if (lookAspect > containerAspect) { cw = width; ch = Math.round(width / lookAspect); }
  else { ch = height; cw = Math.round(height * lookAspect); }

  // Check for a cached rendered thumbnail
  const cachedUrl = look.id ? getCachedThumbnail(look.id, cw, ch) : null;

  // Load ref photo via downscaled cache — use thumb as fallback when full data is omitted
  const refPhotoSrc = look.ref_photo_data || look.ref_photo_thumb;
  useEffect(() => {
    if (cachedUrl) return;
    setRefImg(null);
    getRefImage(refPhotoSrc).then(img => { if (img) setRefImg(img); });
  }, [refPhotoSrc, cachedUrl]);

  // Load collage background reference image via downscaled cache
  useEffect(() => {
    if (cachedUrl) return;
    setCanvasBgRefImg(null);
    if (look.canvas_bg_ref_data) {
      getRefImage(look.canvas_bg_ref_data).then(img => { if (img) setCanvasBgRefImg(img); });
    }
  }, [look.canvas_bg_ref_data, cachedUrl]);

  // Load overlay from S3 — independent effect (no shared cancellation)
  const overlayKey = (look.overlay_s3_path && look.overlay_filename)
    ? look.overlay_s3_path + (look.overlay_s3_path.endsWith('/') ? '' : '/') + look.overlay_filename
    : null;

  useEffect(() => {
    if (cachedUrl) return;
    if (!overlayKey) { setOverlayImg(null); return; }
    const cached = getCachedImage(overlayKey);
    if (cached) { setOverlayImg(cached); return; }
    loadImage(overlayKey)
      .then(img => setOverlayImg(img))
      .catch(() => setOverlayImg(null));
  }, [overlayKey, cachedUrl]);

  // Load canvas background from S3 — independent effect
  const canvasBgKey = (look.canvas_filepath && look.canvas_filename)
    ? look.canvas_filepath + (look.canvas_filepath.endsWith('/') ? '' : '/') + look.canvas_filename
    : null;

  useEffect(() => {
    if (cachedUrl) return;
    if (!canvasBgKey) { setCanvasBgImg(null); return; }
    const cached = getCachedImage(canvasBgKey);
    if (cached) { setCanvasBgImg(cached); return; }
    loadImage(canvasBgKey)
      .then(img => setCanvasBgImg(img))
      .catch(() => setCanvasBgImg(null));
  }, [canvasBgKey, cachedUrl]);

  // Load custom fonts used by text fields
  useEffect(() => {
    if (cachedUrl) return;
    (look.text_fields || []).forEach(tf => {
      if (tf.font_filename && !loadedFonts.has(tf.font_filename)) {
        loadFont(tf.font_filename, tf.font_s3_path).then(() => {
          setFontLoadTick(t => t + 1);
        });
      }
    });
  }, [look.text_fields, cachedUrl]);

  // Load collage photo images — render full composites for look templates, load database poses
  useEffect(() => {
    if (cachedUrl) return;
    if (look.look_type !== 'collage') return;
    (look.collage_photos || []).forEach(photo => {
      // Look template source — render full composite (overlay, text, bg, ref photo)
      if (photo.look_ref && photo.source_type !== 'database') {
        if (collagePhotoImgs[photo.look_ref]) return;
        if (!allLooks) return;
        const refLook = allLooks.find(l => l.name === photo.look_ref);
        if (!refLook) return;
        renderLookComposite(refLook, 480, 480, allLooks).then(img => {
          if (img) setCollagePhotoImgs(prev => ({ ...prev, [photo.look_ref]: { img, look: refLook } }));
        });
      }
      // Database pose source
      if (photo.source_type === 'database' && photo.pose_thumbnail_url) {
        const key = `db_${photo.pose_id}`;
        if (collagePhotoImgs[key]) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setCollagePhotoImgs(prev => ({ ...prev, [key]: { img, look: null } }));
        };
        img.src = photo.pose_thumbnail_url;
      }
    });
  }, [look.collage_photos, look.look_type, allLooks, cachedUrl]);

  // Draw canvas (skip if cached thumbnail exists)
  useEffect(() => {
    if (cachedUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    if (transparent) { ctx.clearRect(0, 0, cw, ch); }
    else { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch); }

    // Background and ref photo are always-present implicit layers drawn first,
    // matching the editor which renders them outside the layer_order loop.
    // layer_order only controls the order of interactive elements (overlay, text, collage photos).
    if (canvasBgRefImg) { ctx.drawImage(canvasBgRefImg, 0, 0, cw, ch); }
    else if (canvasBgImg) { ctx.drawImage(canvasBgImg, 0, 0, cw, ch); }

    if (refImg) {
      const imgAspect = refImg.width / refImg.height;
      const canAspect = cw / ch;
      const refZoom = look.ref_zoom ?? 1;
      const refRotation = look.ref_rotation ?? 0;
      const refOffsetX = look.ref_offset_x ?? 0;
      const refOffsetY = look.ref_offset_y ?? 0;

      let dw, dh;
      if (imgAspect > canAspect) { dh = ch; dw = dh * imgAspect; }
      else { dw = cw; dh = dw / imgAspect; }
      dw *= refZoom;
      dh *= refZoom;

      const cx = cw / 2 + refOffsetX * cw;
      const cy = ch / 2 + refOffsetY * ch;

      ctx.save();
      ctx.translate(cx, cy);
      if (refRotation !== 0) ctx.rotate(refRotation * Math.PI / 180);
      ctx.globalAlpha = transparent ? 0.85 : 0.5;
      ctx.drawImage(refImg, -dw / 2, -dh / 2, dw, dh);
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    // Render interactive layers in layer_order (or default order if none saved)
    const layerOrder = (look.layer_order && look.layer_order.length > 0)
      ? look.layer_order
      : [{ type: 'overlay' }, { type: 'text' }];

    // Helper to draw a single collage photo
    const drawCollagePhoto = (photo, i) => {
      if (photo.x == null || photo.width == null) return;
      const px = photo.x * cw, py = photo.y * ch, pw = photo.width * cw, ph = photo.height * ch;
      const PHOTO_COLORS = ['#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0'];
      const color = PHOTO_COLORS[i % PHOTO_COLORS.length];
      const imgKey = photo.source_type === 'database' ? `db_${photo.pose_id}` : photo.look_ref;
      const photoData = imgKey ? collagePhotoImgs[imgKey] : null;
      if (photoData && photoData.img) {
        const pImg = photoData.img;
        ctx.save(); ctx.beginPath(); ctx.rect(px, py, pw, ph); ctx.clip();
        const iA = pImg.width / pImg.height, rA = pw / ph;
        let ddw, ddh;
        if (iA > rA) { ddh = ph; ddw = ddh * iA; } else { ddw = pw; ddh = ddw / iA; }
        ctx.drawImage(pImg, px + (pw - ddw) / 2, py + (ph - ddh) / 2, ddw, ddh);
        ctx.restore();
      } else {
        ctx.fillStyle = color + '20'; ctx.fillRect(px, py, pw, ph);
        ctx.strokeStyle = color + '60'; ctx.lineWidth = 1; ctx.strokeRect(px, py, pw, ph);
        const fs = Math.max(6, Math.min(10, pw * 0.15));
        ctx.fillStyle = color; ctx.font = `${fs}px sans-serif`; ctx.textAlign = 'center';
        const pLabel = photo.source_type === 'database' ? (photo.pose_name || 'DB') : (photo.look_ref || `P${i + 1}`);
        ctx.fillText(pLabel, px + pw / 2, py + ph / 2 + fs * 0.35);
        ctx.textAlign = 'start';
      }
    };

    // Helper to draw a single text field
    const drawTextField = (tf) => {
      if (tf.center_x != null && tf.center_y != null) {
        const tx = tf.center_x * cw, ty = tf.center_y * ch;
        const left = (tf.bound_x_left || 0) * cw, right = (tf.bound_x_right || 1) * cw;
        const fs = Math.min(16, Math.max(6, (tf.font_size_max || 160) * ch / (look.canvas_height || 3000)));
        const familyName = tf.font_filename ? tf.font_filename.replace(/\.[^.]+$/, '') : 'sans-serif';
        ctx.font = `${tf.italic ? 'italic ' : ''}${tf.bold ? 'bold ' : ''}${fs}px ${familyName}, sans-serif`;
        ctx.fillStyle = tf.font_color || '#000';
        if (tf.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 2; ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1; }
        ctx.textAlign = tf.align || 'center';
        const ax = tf.align === 'left' ? left + 2 : tf.align === 'right' ? right - 2 : tx;
        ctx.fillText(tf.content || '', ax, ty + fs * 0.35);
        ctx.textAlign = 'start';
        if (tf.shadow) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; }
      }
    };

    let collageDrawn = false, overlayDrawn = false, textDrawn = false;

    layerOrder.forEach(layer => {
      if (layer.type === 'collage_photo' && look.look_type === 'collage') {
        const photo = (look.collage_photos || [])[layer.index];
        if (photo) drawCollagePhoto(photo, layer.index);
        collageDrawn = true;
      }

      if (layer.type === 'overlay') {
        if (overlayImg) { ctx.drawImage(overlayImg, 0, 0, cw, ch); }
        overlayDrawn = true;
      }

      if (layer.type === 'text') {
        if (layer.index != null) {
          const tf = (look.text_fields || [])[layer.index];
          if (tf) drawTextField(tf);
        } else {
          (look.text_fields || []).forEach(tf => drawTextField(tf));
        }
        textDrawn = true;
      }
    });

    // Draw any elements not covered by layer_order (fallback for missing entries)
    if (!collageDrawn && look.look_type === 'collage') {
      (look.collage_photos || []).forEach((photo, i) => drawCollagePhoto(photo, i));
    }
    if (!overlayDrawn && overlayImg) {
      ctx.drawImage(overlayImg, 0, 0, cw, ch);
    }
    if (!textDrawn && (look.text_fields || []).length > 0) {
      (look.text_fields || []).forEach(tf => drawTextField(tf));
    }

    // Face placement oval — only shown when showGuides is true (e.g., layout slot previews)
    if (showGuides && look.eyes_y != null && look.face_height != null && look.center_x != null) {
      const fovCx = look.center_x * cw;
      const fovEy = look.eyes_y * ch;
      const fovFh = look.face_height * ch;
      const fovOvalCY = fovEy + fovFh * 0.1;
      const fovRx = fovFh * 0.35;
      const fovRy = fovFh / 2;
      const fovRot = (look.rotation || 0) * Math.PI / 180;
      ctx.save();
      ctx.translate(fovCx, fovOvalCY);
      ctx.rotate(fovRot);
      ctx.strokeStyle = 'rgba(233, 30, 99, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 2]);
      ctx.beginPath();
      ctx.ellipse(0, 0, fovRx, fovRy, 0, 0, Math.PI * 2);
      ctx.stroke();
      const fovEyeLocalY = fovEy - fovOvalCY;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(-4, fovEyeLocalY);
      ctx.lineTo(4, fovEyeLocalY);
      ctx.moveTo(0, fovEyeLocalY - 4);
      ctx.lineTo(0, fovEyeLocalY + 4);
      ctx.stroke();
      ctx.restore();
    }

    if (showGuides && !transparent && look.mascot_center_x != null && look.mascot_center_y != null && look.mascot_size != null) {
      const mx = look.mascot_center_x * cw, my = look.mascot_center_y * ch, ms = look.mascot_size * cw;
      ctx.strokeStyle = 'rgba(76, 175, 80, 0.5)'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]); ctx.strokeRect(mx - ms / 2, my - ms / 2, ms, ms); ctx.setLineDash([]);
    }

    // Cache the rendered thumbnail — only when all async images have loaded.
    const hasRefSrc = !!(look.ref_photo_data || look.ref_photo_thumb);
    const refReady = !hasRefSrc || refImg;
    const hasBgSrc = !!(look.canvas_bg_ref_data || canvasBgKey);
    const bgReady = !hasBgSrc || canvasBgRefImg || canvasBgImg;
    const collagePhotos = (look.look_type === 'collage' && look.collage_photos) || [];
    const expectedPhotoKeys = collagePhotos
      .map(p => p.source_type === 'database' ? (p.pose_id ? `db_${p.pose_id}` : null) : (p.look_ref || null))
      .filter(Boolean);
    const collageReady = expectedPhotoKeys.length === 0 || expectedPhotoKeys.every(k => collagePhotoImgs[k]);
    const hasOverlaySrc = !!overlayKey;
    const overlayReady = !hasOverlaySrc || overlayImg;
    // Check that custom fonts are loaded before caching
    const fontsNeeded = (look.text_fields || []).filter(tf => tf.font_filename).map(tf => tf.font_filename);
    const fontsReady = fontsNeeded.length === 0 || fontsNeeded.every(f => loadedFonts.has(f));
    if (look.id && !transparent && refReady && bgReady && collageReady && overlayReady && fontsReady) {
      try { setCachedThumbnail(look.id, cw, ch, canvas.toDataURL('image/png')); }
      catch (e) { /* tainted canvas */ }
    }
  }, [look, refImg, canvasBgRefImg, overlayImg, canvasBgImg, collagePhotoImgs, cw, ch, transparent, showGuides, cachedUrl, fontLoadTick]);

  // If cached, render an img tag instead of a canvas
  if (cachedUrl) {
    return (
      <img
        src={cachedUrl}
        width={cw}
        height={ch}
        alt={look.name || 'Look preview'}
        style={{ width: cw, height: ch, display: 'block', borderRadius: 2, margin: '0 auto' }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={cw}
      height={ch}
      style={{ width: cw, height: ch, display: 'block', borderRadius: 2, margin: '0 auto' }}
    />
  );
});

LookPreviewCanvas.displayName = 'LookPreviewCanvas';

export default LookPreviewCanvas;
